import styled from "@emotion/styled"
import { useMemo } from "react"
import qrcode from "qrcode-generator"

import {
    Dots, MsAvatar, MsChip, MsDeck, MsDisplay, MsEyebrow, MsPaper, MsPlate,
    MsQmark, MsQtext, MsRuleRed, MsStamp, MsTape, ServerTimerRule,
} from "../ms"
import { paper, shade } from "../theme"

/**
 * The TV's scale layer.
 *
 * Every one of these is the phone's own primitive with its *size* restated and
 * nothing else. That's the whole trick: face, weight, letter-spacing, colour,
 * tone ramp, shadow language and motion all keep coming from ms.tsx, so the two
 * surfaces cannot drift — a change to how an eyebrow or a quote is set lands on
 * the television at the same time as the handset. Only the numbers that depend
 * on viewing distance live here.
 *
 * The rule when adding to this file: if you find yourself writing `font-family`
 * or a colour, you're reimplementing a primitive instead of scaling one.
 */

/* ── Type ───────────────────────────────────────────────────────────────── */

/**
 * The serif voice. Size varies per screen, so it takes one rather than guessing.
 *
 * `weight` is here because the phone drops MsDisplay to 700 for quoted answers
 * (Results, VotingResults) and keeps 800 for headings — a distinction worth
 * carrying over, and cheaper as a prop than as four styled() subclasses.
 */
export const TvDisplay = styled(MsDisplay)<{ size: number; weight?: number }>`
    font-size: ${p => p.size}px;
    ${p => p.weight != null && `font-weight: ${p.weight};`}
`

export const TvDeck = styled(MsDeck)<{ size?: number }>`
    font-size: ${p => p.size ?? 26}px;
`

export const TvEyebrow = styled(MsEyebrow)`
    font-size: 16px;
    letter-spacing: 0.06em;
`

export const TvStamp = styled(MsStamp)`
    font-size: 18px;
    letter-spacing: 0.1em;
    padding: 10px 20px;
    border-width: 3px;
    border-radius: 5px;
`

export const TvChip = styled(MsChip)`
    font-size: 22px;
    padding: 10px 20px;
    border-radius: 999px;
`

export const TvAvatar = styled(MsAvatar)<{ size?: number }>`
    width: ${p => p.size ?? 48}px;
    height: ${p => p.size ?? 48}px;
    font-size: ${p => Math.round((p.size ?? 48) * 0.38)}px;
`

export const TvRuleRed = styled(MsRuleRed)`
    height: 6px;
    width: 80px;
    border-radius: 3px;
`

/* ── Surfaces ───────────────────────────────────────────────────────────── */

export const TvPaper = styled(MsPaper)`
    padding: 44px 52px;
    border-radius: 8px;
`

export const TvPlate = styled(MsPlate)`
    padding: 32px 40px;
    border-radius: 10px;
`

export const TvTape = styled(MsTape)`
    width: 140px;
    height: 36px;
    top: -18px;
`

/* ── Quotes ─────────────────────────────────────────────────────────────── */

/** The entry card's opening mark and body, at reading-from-the-sofa size. */
export const TvQmark = styled(MsQmark)`
    font-size: 96px;
    line-height: 0.4;
    height: 40px;
`

export const TvQtext = styled(MsQtext)<{ size?: number }>`
    font-size: ${p => p.size ?? 36}px;
    line-height: 1.2;
`

/* ── Loading ────────────────────────────────────────────────────────────── */

/** The phone's dot loader, grown. Scaled rather than redrawn so the cadence —
 *  which is what actually reads as "thinking" — stays identical. */
const DotScale = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* A transform doesn't change layout, so the box has to be told the size the
       dots will actually occupy — otherwise they scale straight out of whatever
       row they're sitting in and get clipped. */
    width: 64px;
    flex: none;
    transform: scale(2.2);
    transform-origin: center;
`

export function TvDots() {
    return <DotScale><Dots /></DotScale>
}

/* ── Timer ──────────────────────────────────────────────────────────────── */

/**
 * The same ruled countdown the phone runs, scaled up.
 *
 * Wrapped rather than reimplemented: the tick cadence, the visibility handling,
 * the warn threshold and the way the rule glides between seconds are all
 * behaviour, and behaviour is the last thing two surfaces should each own a copy
 * of. A TV just needs it bigger.
 */
const TimerScale = styled.div`
    /* An explicit width, because ServerTimerRule's track is \`flex: 1\` inside a
       \`width: 100%\` row — dropped into an auto-width flex parent it resolves to
       nothing and only the numerals survive. This is the width *before* the
       scale below multiplies it. */
    width: 300px;
    transform: scale(2.2);
    transform-origin: right center;
    /* Reserve the scaled height so the band doesn't collapse around it. */
    height: 44px;
    display: flex;
    align-items: center;

    /* The rule carries the phone's own outer spacing — safe-area top padding and
       22px gutters. On a band that already has gutters those are a hole, not a
       margin, so they're flattened here rather than in the shared component. */
    > * {
        padding: 0;
        max-width: none;
    }
`

export function TvTimer({ timeoutTime, phaseStartTime }: {
    timeoutTime: number | null | undefined
    phaseStartTime: number | null | undefined
}) {
    if (!timeoutTime || !phaseStartTime) return null
    return (
        <TimerScale>
            <ServerTimerRule timeoutTime={timeoutTime} phaseStartTime={phaseStartTime} />
        </TimerScale>
    )
}

/* ── QR ─────────────────────────────────────────────────────────────────── */

const QrFrame = styled.div`
    position: relative;
    background: ${paper.paperCool};
    padding: 14px;
    border-radius: 6px;
    box-shadow: 0 0 0 1px ${shade(0.12)}, 0 6px 14px -6px ${shade(0.3)};
    line-height: 0;
`

/**
 * A real QR of the join URL.
 *
 * Error-correction level H (~30% recoverable) rather than the usual M: this code
 * gets read across a room, at an angle, off a glossy panel, by whoever is
 * holding their phone least steadily — and the extra redundancy costs nothing
 * but a slightly denser grid at a size we control.
 */
export function TvQr({ value, size = 260 }: { value: string; size?: number }) {
    const path = useMemo(() => {
        const qr = qrcode(0, "H")
        qr.addData(value)
        qr.make()
        const count = qr.getModuleCount()
        let d = ""
        for (let row = 0; row < count; row++) {
            for (let col = 0; col < count; col++) {
                if (qr.isDark(row, col)) d += `M${col} ${row}h1v1h-1z`
            }
        }
        return { d, count }
    }, [value])

    return (
        <QrFrame style={{ width: size, height: size }}>
            <svg width={size - 28} height={size - 28} viewBox={`0 0 ${path.count} ${path.count}`}>
                <rect width={path.count} height={path.count} fill={paper.paperCool} />
                <path d={path.d} fill={paper.ink} />
            </svg>
        </QrFrame>
    )
}
